import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIError } from '@/api/client';
import type {
  JobBreakdown,
  BreakdownVersionWithUser,
  BreakdownVersionInput,
  PatchBreakdownInput,
  PatchBreakdownResponse,
  SaveBreakdownInput,
} from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';
import { invalidateJobBilling } from '@/hooks/jobBillingKeys';

// API response types
interface BreakdownResponse extends JobBreakdown {}

interface VersionsResponse {
  versions: Array<Omit<BreakdownVersionWithUser, 'snapshot'>>;
}

interface VersionResponse {
  version: BreakdownVersionWithUser;
}

interface RestoreResponse extends JobBreakdown {
  restoredFromVersion: {
    id: string;
    name: string;
  };
  taxRate: number;
  markupRate: number;
}

// Query keys
export const breakdownKeys = {
  all: ['breakdown'] as const,
  detail: (jobId: string) => [...breakdownKeys.all, 'detail', jobId] as const,
  versions: (jobId: string) => [...breakdownKeys.all, 'versions', jobId] as const,
  version: (jobId: string, versionId: string) => [...breakdownKeys.versions(jobId), versionId] as const,
};

// ============================================================================
// Main Breakdown Queries/Mutations
// ============================================================================

// Fetch full breakdown for a job. When `docId` is supplied (flag-on word-
// processor path) the backend reads that document specifically; otherwise it
// returns the slot's active doc (legacy/default behavior). The docId is part
// of the query key so each opened doc gets its own cache entry — without it
// the editor would render whichever doc was fetched first.
export function useBreakdown(jobId: string | undefined, docId?: string) {
  return useQuery({
    queryKey: docId
      ? [...breakdownKeys.detail(jobId!), docId]
      : breakdownKeys.detail(jobId!),
    queryFn: async () => {
      const url = docId
        ? `/jobs/${jobId}/breakdown?docId=${docId}`
        : `/jobs/${jobId}/breakdown`;
      const response = await apiClient.get<BreakdownResponse>(url);
      return response;
    },
    enabled: !!jobId,
  });
}

// Patch-based save - only sends changes
export function usePatchBreakdown(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: PatchBreakdownInput) => {
      const response = await apiClient.patch<PatchBreakdownResponse>(
        `/jobs/${jobId}/breakdown`,
        data
      );
      return response;
    },
    // The editor keeps its own state, so this save deliberately does NOT
    // invalidate the breakdown itself. The job's billing rollup is a different
    // matter: the server recomputes the active document's total_cost on every
    // save, and that total is the Billing tab's contract value and an input to
    // the billing state (NEW-242).
    onSuccess: () => {
      invalidateJobBilling(queryClient, jobId);
    },
    onError: (error: Error) => {
      // Don't show toast for errors the component handles directly
      if (error instanceof APIError && (error.code === 'DUPLICATE_ID' || error.code === 'FK_VIOLATION')) {
        return;
      }
      toast({
        title: 'Error',
        description: error.message || 'Failed to save changes. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

// Full save - sends entire breakdown state, backend diffs
export function useSaveBreakdown(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: SaveBreakdownInput) => {
      const response = await apiClient.put<BreakdownResponse & { taxRate: number; markupRate: number; roundingPrecision: number | null }>(
        `/jobs/${jobId}/breakdown`,
        data
      );
      return response;
    },
    // Same reason as usePatchBreakdown: the rates travel with this payload, so
    // the document total can move even when no line item did.
    onSuccess: () => {
      invalidateJobBilling(queryClient, jobId);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error saving',
        description: error.message || 'Failed to save breakdown. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Version Control Queries/Mutations
// ============================================================================

// Fetch all versions for a job
export function useBreakdownVersions(jobId: string | undefined) {
  return useQuery({
    queryKey: breakdownKeys.versions(jobId!),
    queryFn: async () => {
      const response = await apiClient.get<VersionsResponse>(
        `/jobs/${jobId}/breakdown/versions`
      );
      return response.versions;
    },
    enabled: !!jobId,
  });
}

// Fetch a specific version
export function useBreakdownVersion(jobId: string | undefined, versionId: string | undefined) {
  return useQuery({
    queryKey: breakdownKeys.version(jobId!, versionId!),
    queryFn: async () => {
      const response = await apiClient.get<VersionResponse>(
        `/jobs/${jobId}/breakdown/versions/${versionId}`
      );
      return response.version;
    },
    enabled: !!jobId && !!versionId,
  });
}

// Save current state as a new version
export function useSaveBreakdownVersion(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: BreakdownVersionInput) => {
      const response = await apiClient.post<VersionResponse>(
        `/jobs/${jobId}/breakdown/versions`,
        data
      );
      return response.version;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: breakdownKeys.versions(jobId) });
      toast({
        title: 'Snapshot saved',
        description: 'A snapshot of your breakdown has been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save snapshot.',
        variant: 'destructive',
      });
    },
  });
}

// Restore a version to the working tables
export function useRestoreBreakdownVersion(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (versionId: string) => {
      const response = await apiClient.post<RestoreResponse>(
        `/jobs/${jobId}/breakdown/versions/${versionId}/restore`
      );
      return response;
    },
    onSuccess: (data) => {
      // Update the breakdown cache with restored data (including rates)
      queryClient.setQueryData(breakdownKeys.detail(jobId), {
        lineItems: data.lineItems,
        projects: data.projects,
        totalCost: data.totalCost,
        taxRate: data.taxRate,
        markupRate: data.markupRate,
        roundingPrecision: data.roundingPrecision,
      });
      // A restore replaces the working set wholesale — the contract value with it.
      invalidateJobBilling(queryClient, jobId);
      toast({
        title: 'Snapshot restored',
        description: `Restored from "${data.restoredFromVersion.name}".`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to restore snapshot.',
        variant: 'destructive',
      });
    },
  });
}

// Update a version's name
export function useUpdateBreakdownVersion(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ versionId, name, tag }: { versionId: string; name: string; tag?: string | null }) => {
      const response = await apiClient.patch<{ version: Omit<BreakdownVersionWithUser, 'snapshot'> }>(
        `/jobs/${jobId}/breakdown/versions/${versionId}`,
        { name, tag: tag ?? null }
      );
      return response.version;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: breakdownKeys.versions(jobId) });
      toast({
        title: 'Snapshot updated',
        description: 'The snapshot has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update snapshot.',
        variant: 'destructive',
      });
    },
  });
}

// Delete a version
export function useDeleteBreakdownVersion(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (versionId: string) => {
      await apiClient.delete(`/jobs/${jobId}/breakdown/versions/${versionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: breakdownKeys.versions(jobId) });
      toast({
        title: 'Snapshot deleted',
        description: 'The snapshot has been deleted.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete snapshot.',
        variant: 'destructive',
      });
    },
  });
}
